/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {Offer} from 'types/entities/Offer'
import {getShortNameFromId} from 'utils/reservationUtils'
import Dates from 'utils/Dates'
import {DATE_FORMATS} from 'constants/DateFormats'
import {PencilIcon, PencilSquareIcon} from '@heroicons/react/24/outline'
import UiButton, {UiButtonSize, UiButtonStyle, UiButtonType} from 'components/Ui/UiButton'
import {copyToClipboard} from 'utils/utility'
import UiBadge from 'components/Ui/UiBadge'
import UiOfferStatusBadgeGroup from 'components/Offers/UiOfferStatusBadgeGroup'
import UiAlert from 'components/Ui/UiAlert'
import {OfferStatus} from 'types/enums/OfferStatus'
import {getCompanyDisplayName} from 'utils/companyUtils'
import {Tooltip} from 'react-tooltip'
import {useOptionalOfferDetailContext} from 'pages/Front/Offers/components/OfferDetailContext'
import UiProbabilityPicker from 'components/Ui/UiProbabilityPicker'
import {
    OFFER_DETAIL_EDIT_DISABLED_TOOLTIP_ID,
    useOfferDetailInfoActions,
} from 'utils/hooks/useOfferDetailInfoActions'

interface OfferDetailInfoProps {
    selectedOffer?: Offer
}

const OfferDetailInfo = React.forwardRef<HTMLDivElement, OfferDetailInfoProps>(
    ({ selectedOffer: selectedOfferProp }, ref) => {
    const { t } = useTranslation()
    const offerDetailContext = useOptionalOfferDetailContext()
    const selectedOffer = selectedOfferProp ?? offerDetailContext?.selectedOffer
    const {
        acceptedEditTooltip,
        hasConfirmedReservation,
        isEditLocked,
        isOfferInReconfirm,
        showOpportunityRejection,
        rejectionComment,
        rejectionOtherComment,
        rejectionReasonLabels,
        handleStatusChange,
        handleRejectionDetailsEdit,
        handleProbabilityChange,
        handleCompanyEdit,
        handleContactEdit,
        handleValidUntilEdit,
        handleEventDateEdit,
        handlePersonsCountEdit,
        handleAddressEdit,
    } = useOfferDetailInfoActions(selectedOffer)

    if (!selectedOffer) {
        return null
    }

    return (
        <div ref={ref} className="flex flex-col gap-3">
            <div className="UiCard p-4">
                <UiOfferStatusBadgeGroup value={selectedOffer.status as OfferStatus} onChange={handleStatusChange} />
            </div>

            {hasConfirmedReservation && !isOfferInReconfirm && (
                <UiAlert
                    variant="warning"
                    description={t('This offer already has a booking. Offer items are locked, but customer can still be changed.')}
                />
            )}

            {hasConfirmedReservation && isOfferInReconfirm && (
                <UiAlert
                    variant="info"
                    description={t('Reconfirm is active. Offer item changes are enabled and can be saved to update the booking.')}
                />
            )}

            {(showOpportunityRejection || selectedOffer.status === OfferStatus.Rejected) && (
                <UiAlert
                    variant="error"
                    title={
                        <div className="flex items-center justify-between gap-2">
                            <span>{t('Reasons for Rejection (Multiple Selections Possible)')}</span>
                            <UiButton
                                onClick={handleRejectionDetailsEdit}
                                style={UiButtonStyle.Secondary}
                                size={UiButtonSize.Small}
                                type={UiButtonType.Button}
                                icon={<PencilSquareIcon className="w-4 h-4" />}
                            >
                                {t('Edit')}
                            </UiButton>
                        </div>
                    }
                    description={
                        <div className="flex flex-col gap-2 mt-1">
                            {showOpportunityRejection && (
                                <p className="text-sm">
                                    {t(
                                        'Rejection applies to the entire opportunity and all linked offers.'
                                    )}
                                </p>
                            )}
                            <ul className="list-disc ml-5">
                                {rejectionReasonLabels.length > 0 ? (
                                    rejectionReasonLabels.map((reason) => <li key={reason.id}>{reason.label}</li>)
                                ) : (
                                    <li>—</li>
                                )}
                            </ul>
                            {rejectionOtherComment && (
                                <div>
                                    <span className="font-semibold">{t('Other reason')}:</span> {rejectionOtherComment}
                                </div>
                            )}
                            {rejectionComment && (
                                <div>
                                    <span className="font-semibold">{t('Optional comment')}:</span> {rejectionComment}
                                </div>
                            )}
                        </div>
                    }
                />
            )}

            <InfoCard className={clsx('OfferDetailInfo')} title={t('Offer Information')}>
                <InfoRow
                    className={'CategoryRow'}
                    label={t('Ticket type')}
                    value={getShortNameFromId(selectedOffer.packagesCart?.category) ?? '–'}
                />

                <InfoRow
                    className={'ProbabilityRow'}
                    label={t('Probability')}
                    value={
                        <UiProbabilityPicker
                            value={selectedOffer.probability ?? 50}
                            onChange={handleProbabilityChange}
                            disabled={isEditLocked}
                        />
                    }
                />

                <InfoRow
                    className={'GuidRow'}
                    label={t('GUID')}
                    value={
                        <UiBadge variant="info" onClick={() => copyToClipboard(selectedOffer.guid)}>
                            {selectedOffer.guid ?? '–'}
                        </UiBadge>
                    }
                />
                <InfoRow
                    className={'CompanyRow'}
                    label={t('Company')}
                    onEdit={handleCompanyEdit}
                    value={
                        <div className="text-xs font-medium">
                            {selectedOffer.company_id ? (
                                <>
                                    {getCompanyDisplayName(selectedOffer.company) || ''} (ID: {selectedOffer.company_id})
                                </>
                            ) : (
                                <span className="text-red-500 font-black animate-pulse uppercase text-xs">
                                    {t('No company')}
                                </span>
                            )}
                        </div>
                    }
                />
                <InfoRow
                    className={'CompanyRow'}
                    label={t('Offer number')}
                    value={
                        <UiBadge
                            variant="info"
                            onClick={() => copyToClipboard('' + selectedOffer.number)}
                        >
                            {selectedOffer.number ?? '–'}
                        </UiBadge>
                    }
                />
                <InfoRow
                    className={'ContactRow'}
                    label={t('Contact person')}
                    onEdit={handleContactEdit}
                    value={
                        selectedOffer.contact
                            ? `${selectedOffer.contact.first_name} ${selectedOffer.contact.last_name}`
                            : '–'
                    }
                />
                <InfoRow
                    className={'ValidUntilRow'}
                    label={t('Valid Until')}
                    onEdit={handleValidUntilEdit}
                    editDisabled={isEditLocked}
                    editDisabledTooltip={acceptedEditTooltip}
                    value={
                        selectedOffer.valid_until
                            ? Dates.dayjs(selectedOffer.valid_until).format(DATE_FORMATS.DATE)
                            : t('No validity limit')
                    }
                />
                <InfoRow
                    className={'EventDateRow'}
                    label={t('Event date')}
                    onEdit={handleEventDateEdit}
                    editDisabled={isEditLocked}
                    editDisabledTooltip={acceptedEditTooltip}
                    value={
                        selectedOffer.packagesCart?.date
                            ? `${Dates.dayjs(selectedOffer.packagesCart.date).format(DATE_FORMATS.DATE)} ${
                                  selectedOffer.packagesCart?.hour !== undefined &&
                                  selectedOffer.packagesCart?.minute !== undefined
                                      ? `${String(selectedOffer.packagesCart.hour).padStart(2, '0')}:${String(selectedOffer.packagesCart.minute).padStart(2, '0')}`
                                      : ''
                              }`.trim()
                            : '–'
                    }
                />
                <InfoRow
                    className={'PersonsCountRow'}
                    label={t('Persons count')}
                    onEdit={handlePersonsCountEdit}
                    editDisabled={isEditLocked}
                    editDisabledTooltip={acceptedEditTooltip}
                    value={
                        (selectedOffer.packagesCart?.drivers_count_adult ?? 0) +
                        (selectedOffer.packagesCart?.drivers_count_child ?? 0)
                    }
                />
                <InfoRow
                    className={'AddressRow'}
                    label={t('Address')}
                    onEdit={handleAddressEdit}
                    value={
                        selectedOffer.address
                            ? `${selectedOffer.address.street_name}, ${selectedOffer.address.town}`
                            : '–'
                    }
                />
            </InfoCard>
            <Tooltip id={OFFER_DETAIL_EDIT_DISABLED_TOOLTIP_ID} style={{ zIndex: 99999 }} />
        </div>
    )
})

const InfoCard: React.FC<{
    title: string
    className?: string
    children: React.ReactNode
}> = ({ title, className, children }) => {
    return (
        <div className={clsx(className, 'UiCard p-2 flex flex-col')}>
            <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 pb-1 border-b border-gray-200 dark:border-gray-700">
                {title}
            </h3>
            <div className="grid grid-cols-2 gap-x-12 gap-y-1.5 text-xs">{children}</div>
        </div>
    )
}

const InfoRow: React.FC<{
    label: React.ReactNode
    value: React.ReactNode
    className?: string
    onEdit?: () => void
    editDisabled?: boolean
    editDisabledTooltip?: string
}> = ({ label, value, className, onEdit, editDisabled, editDisabledTooltip }) => {
    return (
        <div className={clsx(className, 'flex justify-between items-center gap-2 min-h-6')}>
            <span className="text-gray-600 dark:text-gray-400 shrink-0">{label}</span>
            <div className="flex items-center gap-2 justify-end">
                <span className="text-gray-900 dark:text-gray-100 font-medium text-right">{value}</span>
                {onEdit && (
                    <span
                        data-tooltip-id={editDisabled ? OFFER_DETAIL_EDIT_DISABLED_TOOLTIP_ID : undefined}
                        data-tooltip-content={editDisabled ? editDisabledTooltip : undefined}
                        className="inline-flex"
                    >
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation()
                                if (editDisabled) return
                                onEdit()
                            }}
                            disabled={!!editDisabled}
                            className="p-1 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 rounded transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <PencilIcon className="w-3 h-3 text-white" />
                        </button>
                    </span>
                )}
            </div>
        </div>
    )
}

export default OfferDetailInfo
